import type { FrontendInstallContext } from "@p0security/integrations-installer";
import {
  snowflakeAiAccessTokenPrefix,
  snowflakeAiConnectorName,
  snowflakeAiPrivateKeySecretId,
} from "@p0security/integrations-snowflake-ai-shared/secret-manager";
import type { SnowflakeAiComponents } from "@p0security/integrations-snowflake-ai-shared/types";
import {
  awsConnectorTf,
  gcpConnectorTf,
  p0InvokerEmail,
  p0InvokerRoleName,
  secretProviderFor,
  shellLines,
  tfId,
} from "@p0security/integrations-vaulted-credential-shared";
import type { ItemConfigOf } from "@p0security/shared/install/types";

type SecretManagerConfig = ItemConfigOf<
  SnowflakeAiComponents["credential"]
>["secretManager"];

export type AwsSecretManager = Extract<SecretManagerConfig, { type: "aws-sm" }>;

export type GcpSecretManager = Extract<SecretManagerConfig, { type: "gcp-sm" }>;

export type InstallContext = FrontendInstallContext<unknown>;

// copy of the image pin in packages/integrations/datadog-ai/frontend/src/iac.ts
// The build of p0-security-perimeter#373's merge.
const GCP_CONNECTOR_IMAGE = {
  imageName: "docker.io/p0security/p0-connector-snowflake-ai-gcloud",
  digest:
    "sha-0c8e20c@sha256:a219d021ecdda380d83ebcddd52dbd587975312abec17edbf3e9a9618cca8555",
} as const;

// copy of the image pin in packages/integrations/datadog-ai/frontend/src/iac.ts
// The build of p0-security-perimeter#375's merge.
const AWS_CONNECTOR_IMAGE = {
  imageName: "p0security/p0-connector-snowflake-ai-aws",
  digest:
    "sha-c38b5b1@sha256:48cc6f9d2f2b09ef44030855e8ef2b01df114c279ac7e4add29c9f9529b9803f",
} as const;

/** The AWS secret id that the connector reads its private key from. */
const awsPrivateKeySecretId = (accountId: string) =>
  secretProviderFor("aws").replacer(snowflakeAiPrivateKeySecretId(accountId));

/** Sets the private key as the secret's value. The secret itself is created
 * by the connector's Terraform. */
export const awsStorePrivateKey = (
  accountId: string,
  secretManager: AwsSecretManager
) =>
  shellLines(
    "aws secretsmanager put-secret-value",
    `--secret-id ${awsPrivateKeySecretId(accountId)}`,
    `--region ${secretManager.secretsRegion}`,
    "--secret-string file://rsa_key.p8"
  );

/** The connector on Lambda, its private key's secret, and its access to the
 * account's token secrets only. */
export const awsTf = (
  context: InstallContext,
  accountId: string,
  secretManager: AwsSecretManager
) =>
  awsConnectorTf({
    names: { resource: "snowflake_ai", label: "Snowflake" },
    image: AWS_CONNECTOR_IMAGE,
    accountId: secretManager.install,
    region: secretManager.connectorRegion,
    secretsRegion: secretManager.secretsRegion,
    connectorName: snowflakeAiConnectorName,
    p0RoleName: p0InvokerRoleName(context),
    tokenSecretPrefix: secretProviderFor("aws").replacer(
      snowflakeAiAccessTokenPrefix(accountId)
    ),
    installSecret: {
      resource: "private_key",
      itemTfId: tfId(accountId),
      secretId: awsPrivateKeySecretId(accountId),
      label: "private key",
    },
  });

/** The GCP secret id that the connector reads its private key from. */
const gcpPrivateKeySecretId = (accountId: string) =>
  secretProviderFor("gcp").replacer(snowflakeAiPrivateKeySecretId(accountId));

/** Adds the private key as the secret's version. The secret itself is
 * created by the connector's Terraform. */
export const gcpStorePrivateKey = (
  accountId: string,
  secretManager: GcpSecretManager
) =>
  shellLines(
    `gcloud secrets versions add ${gcpPrivateKeySecretId(accountId)}`,
    `--project=${secretManager.install}`,
    "--data-file=rsa_key.p8"
  );

/** The connector on Cloud Run, its private key's secret, and its access to
 * the account's token secrets only. */
export const gcpTf = (
  context: InstallContext,
  accountId: string,
  secretManager: GcpSecretManager
) =>
  gcpConnectorTf({
    // "SnowflakeAi" rather than "Snowflake": a custom role id is per
    // project, and Snowflake (HI) may deploy a connector in the same one.
    names: {
      resource: "snowflake_ai",
      role: "SnowflakeAi",
      label: "Snowflake",
    },
    image: GCP_CONNECTOR_IMAGE,
    projectId: secretManager.install,
    region: secretManager.connectorRegion,
    connectorName: secretManager.connectorServiceName,
    serviceAccountId:
      secretManager.connectorServiceAccount?.split("@")[0] ?? "",
    invokerEmail: p0InvokerEmail(context.installed),
    tokenSecretPrefix: secretProviderFor("gcp").replacer(
      snowflakeAiAccessTokenPrefix(accountId)
    ),
    installSecret: {
      resource: "private_key",
      itemTfId: tfId(accountId),
      secretId: gcpPrivateKeySecretId(accountId),
      label: "private key",
    },
  });

/** The same install through P0's Terraform provider, for a customer who
 * manages P0 in Terraform rather than in this page. */
// TODO(INT-614): placeholder, Miguel decides whether the in-app Terraform
// shows P0's provider resources, whose schema INT-615 defines.
export const p0ProviderTf = (
  accountId: string,
  secretManager: AwsSecretManager | GcpSecretManager
) => {
  const staged = `p0_snowflake_ai_staged.${tfId(accountId)}.secret_manager`;
  const [stagedFields, finalFields] =
    secretManager.type === "aws-sm"
      ? [
          `type           = "aws-sm"
    account_id     = "${secretManager.install}"
    secrets_region = "${secretManager.secretsRegion}"`,
          `type           = ${staged}.type
    account_id     = ${staged}.account_id
    secrets_region = ${staged}.secrets_region`,
        ]
      : [
          `type       = "gcp-sm"
    project_id = "${secretManager.install}"`,
          `type       = ${staged}.type
    project_id = ${staged}.project_id`,
        ];
  return `resource "p0_snowflake_ai_staged" "${tfId(accountId)}" {
  id = "${accountId}"

  secret_manager = {
    ${stagedFields}
  }
}

# Create once the connector is deployed, the Snowflake setup is done, and the
# private key's secret has its value.
resource "p0_snowflake_ai" "${tfId(accountId)}" {
  id = p0_snowflake_ai_staged.${tfId(accountId)}.id

  secret_manager = {
    ${finalFields}
  }
}
`;
};
